import { FC } from "react";
import { CommercialUnitDataHour, TurnoverByHourComparisonItem } from "../../../types/commercial";
import { formatGap, formatKvalue, formatPercentage } from "../../../utils/numberUtils";
import { COLUMN, MagasinOption } from "../Performance.model";
import PerformanceDetailView from "./PerformanceHourTable";

import { useStores } from "../../../context/storesContext";
import { parseStoreHours } from "../../../utils/storeUtils";
import { usePerformance } from "../context/PerformanceContext";
import { useIsCaLiveRoute } from "../../../context/useIsRouteActive";

type MirrorColumn = {
    key: string;
    header: string;
    isVisible: (hasMirror: boolean) => boolean;
};

interface PerformanceByHourApiGroup {
    hour: string;
    ca: number | null | string;
    caN1: number | null | string;
    varN1?: number | null | string;
    proj?: number | null | string;
    prog?: number | null | string;
    shift?: number | null;
    stream?: number | null | string;
    varStream?: number | null | string;
    caMiroir?: number | null | string;
    varN1Miroir?: number | null | string;
    streamMiroir?: number | null | string;
}

interface Row extends PerformanceByHourApiGroup {
    id: string;
}

interface PerformanceByHourProps {
  byHourToday: CommercialUnitDataHour[];
  byHourLastYear: CommercialUnitDataHour[];
  selectedMagasin?: MagasinOption | null;
  range: string;
  isLoading: boolean;
  byHourComparison: TurnoverByHourComparisonItem[];
  isMirrorLoading: boolean;
}

function formatPercent(value: number | null): string {
    if (value === null) return "";
    const formatted = formatPercentage(value, "%", true);
    return value >= 0 ? `+${formatted}` : formatted;
}

function buildRowsFromApi(byHourToday: CommercialUnitDataHour[] = [], byHourLastYear:CommercialUnitDataHour[]= [], openingHour: number,
  closingHour: number): Row[] {
  const allHours = Array.from(new Set([...byHourLastYear.map((l) => l.time), ...byHourToday.map((t) => t.time)]));
  return allHours
  .filter(time => time >= openingHour && time <= closingHour)
  .map((time) =>
  {
    const current = byHourToday.find((currentToday) => currentToday.time === time);
    const lastYear = byHourLastYear.find((lastYear) => lastYear.time === time);

    const currentValue = current ? current.turnover ?? null : null;
    const lastValue = lastYear?.turnover ?? null;
    const currentFlux = current?.ticketsCount ?? null;
    const lastFlux = lastYear?.ticketsCount ?? null;
    const projection = current?.projection ?? null;

    const progPrev = (currentValue ?? 0) - (projection ?? 0);

    let percentChange: number | null = null;
    if (lastValue !== null && lastValue !== 0) {
        const currNum = currentValue === null ? 0 : Number(currentValue);
        percentChange = ((currNum - Number(lastValue)) / Number(lastValue)) * 100;
    }
    let varFlux: number | null = null;
    if (lastFlux !== null && lastFlux !== 0 && currentFlux !== null) {
        varFlux = ((Number(currentFlux) - Number(lastFlux)) / Number(lastFlux)) * 100;
    }
    return {
        id: `hour-${time}`,
        hour: `${parseInt(String(time), 10)}h`,
        ca:   currentValue !== null ? formatKvalue(currentValue) : "",
        caN1: lastValue !== null ? formatKvalue(lastValue) : "",
        varN1: currentValue !== null ? formatPercent(percentChange) : "",
        proj:  projection !== null ? formatKvalue(projection) : "",
        prog:  projection !== null ? formatGap(progPrev) : "",
        stream:  currentFlux !== null ? String(currentFlux) : "",
        varStream: varFlux !== null ? formatPercent(varFlux) : "",
        importedDateTime: (current || lastYear)?.importedDateTime ?? "",
    } as Row;
});
}
function toColumns(mirrorColumns: MirrorColumn[], hasMirror: boolean): COLUMN[] {
    return mirrorColumns
        .filter((col) => col.isVisible(hasMirror))
        .map((col) => ({
            key: col.key,
            header: col.header,
            isVisible: () => true,
        }));
}

function enrichRowsWithMirrorData(
    rows: Row[],
    mirrorComparisonData: TurnoverByHourComparisonItem[] = []
): Row[] {
    return rows.map((row) => {
        const rawHour = row.hour.replace("h", "").padStart(2, "0");
        const mirrorEntry = mirrorComparisonData.find(
            (m) => String(m.time).padStart(2, "0") === rawHour
        );

        const caMiroirValue   = mirrorEntry?.turnover            ?? null;
        const caN1MiroirValue = mirrorEntry?.turnoverComparative ?? null;
        const fluxMiroir      = mirrorEntry?.ticketsCount         ?? null;  // ← nouveau

        let varN1Miroir: number | null = null;
        if (caMiroirValue !== null && caN1MiroirValue !== null && Number(caN1MiroirValue) !== 0) {
            varN1Miroir = ((Number(caMiroirValue) - Number(caN1MiroirValue)) / Number(caN1MiroirValue)) * 100;
        }

        return {
            ...row,
            caMiroir:    caMiroirValue !== null ? formatKvalue(caMiroirValue) : "",
            varN1Miroir: varN1Miroir   !== null ? formatPercent(varN1Miroir)  : "",
            streamMiroir: fluxMiroir    !== null ? String(fluxMiroir)          : "",  // ← FLUX MIROIR
        };
    });
}


const PerformanceHourView: FC<PerformanceByHourProps> = ({
                                                                         byHourToday,
                                                                         byHourLastYear,
                                                                         selectedMagasin,
                                                                          range,
                                                                          isLoading,
                                                                          byHourComparison,
                                                                          isMirrorLoading
                                                                     }) => {
    const hasMirror = !!selectedMagasin;
    const { isCALive } = usePerformance();


    const { selectedStore } = useStores();
    const isCaLiveRoute = useIsCaLiveRoute();

    const { openingHour, closingHour } = !isCaLiveRoute ? parseStoreHours(selectedStore) : { openingHour: 7, closingHour: 21 };
    const baseRows = buildRowsFromApi(byHourToday, byHourLastYear, openingHour + 1, closingHour + 1);

    const isMirrorPending = isMirrorLoading || !byHourComparison || byHourComparison.length === 0;

    const rows = hasMirror
        ? isMirrorPending
            ? baseRows.map((row) => ({
                ...row,
                caMiroir: "",
                varN1Miroir: "",
                streamMiroir: "",
            }))
            : enrichRowsWithMirrorData(baseRows, byHourComparison)
        : baseRows;

  let rangeCol = range === "CUSTOM" ? "Manuel" :range;

  const METRIC_COLUMNS: MirrorColumn[] = [
    {
        key: "hour",
        header: "",
        isVisible: () => true,
    },
    {
        key: "caN1",
        header: `CA ${rangeCol}`,
        isVisible: (hasMirror) => !hasMirror,
    },
    {
        key: "caMiroir",
        header: "CA MIROIR",
        isVisible: (hasMirror) => !!hasMirror,
    },
    {
        key: "ca",
        header: "CA N",
        isVisible: () => true,
    },
    {
        key: "varN1",
        header: isCALive ? "PROG CA" : `VAR ${rangeCol}`,
        isVisible: () => true,
    },
    {
        key: "varN1Miroir",
        header: `VAR ${rangeCol} MIROIR`,
        isVisible: (hasMirror) => !!hasMirror,
    },
    {
        key: "proj",
        header: isCALive ? "BUDGET" : "OBJECTIF",
        isVisible: (hasMirror) => !hasMirror,
    },
    {
        key: "prog",
        header: isCALive ? "R-B" : "ECART",
        isVisible: (hasMirror) => !hasMirror,
    },
    {
        key: "stream",
        header: "Clients N",
        isVisible: () => true,
    },
    {
        key: "streamMiroir",
        header: "Clients MIROIR",
        isVisible: (hasMirror) => !!hasMirror,
    },
    {
        key: "varStream",
        header: isCALive ? "PROG CLIENTS" : "Var Clients",
        isVisible: (hasMirror) => !hasMirror,
    },
  ];

    const visibleColumns = toColumns(METRIC_COLUMNS, hasMirror);
    return (
        <PerformanceDetailView
          data={rows}
          metrics={visibleColumns}
          isLoading={isLoading || !byHourToday || !byHourLastYear || (hasMirror && isMirrorLoading)}
        />
    );
};

export default PerformanceHourView;
